"use client";

import { useEffect } from "react";
import Link from "next/link";
import { ExplanationResult } from "@/app/error-explainer-tool/types";
import CodeBlock from "@/app/error-explainer-tool/components/CodeBlock";
import SiteHeader from "@/app/error-explainer-tool/components/SiteHeader";

interface RelatedError {
  id: string;
  error_message: string;
  language: string;
  slug: string;
  views: number;
}

interface RelatedBlog {
  id: string;
  title: string;
  slug: string;
  language: string;
  views: number;
}

interface ErrorTypeInfo {
  id: string;
  name: string;
  slug: string;
}

interface LanguageInfo {
  id: string;
  name: string;
  slug: string;
}

interface Props {
  slug: string;
  errorMessage: string;
  language: string;
  explanation: ExplanationResult | null;
  views: number;
  createdAt: string;
  related: RelatedError[];
  relatedBlogs: RelatedBlog[];
  errorTypeInfo?: ErrorTypeInfo | null;
  languageInfo?: LanguageInfo | null;
}

function getPopularityLabel(views: number): string | null {
  if (views >= 1000) return "Popular";
  if (views >= 250) return "Trending";
  if (views >= 100) return "Rising";
  return null;
}

const LANGUAGE_COLORS: Record<string, string> = {
  javascript: "text-yellow-400 bg-yellow-400/10 border-yellow-400/20",
  php: "text-violet-400 bg-violet-400/10 border-violet-400/20",
  python: "text-blue-400 bg-blue-400/10 border-blue-400/20",
  java: "text-orange-400 bg-orange-400/10 border-orange-400/20",
  sql: "text-emerald-400 bg-emerald-400/10 border-emerald-400/20",
  other: "text-zinc-400 bg-zinc-400/10 border-zinc-400/20",
};

const SEVERITY_STYLES = {
  critical: { badge: "bg-red-500/15 text-red-400 border-red-500/25", dot: "bg-red-400", label: "Critical" },
  warning: { badge: "bg-amber-500/15 text-amber-400 border-amber-500/25", dot: "bg-amber-400", label: "Warning" },
  info: { badge: "bg-blue-500/15 text-blue-400 border-blue-500/25", dot: "bg-blue-400", label: "Info" },
};

export default function ErrorPageClient({
  slug,
  errorMessage,
  language,
  explanation,
  views,
  createdAt,
  related,
  relatedBlogs,
  errorTypeInfo,
  languageInfo,
}: Props) {
  useEffect(() => {
    fetch(`/api/errors/${slug}/view`, { method: "POST" }).catch(() => {});
  }, [slug]);

  const langColor = LANGUAGE_COLORS[language] || LANGUAGE_COLORS.other;
  const severity = explanation ? SEVERITY_STYLES[explanation.severity] : SEVERITY_STYLES.info;
  const popularity = getPopularityLabel(views);
  const formattedDate = new Date(createdAt).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div
      className="min-h-screen bg-zinc-950 text-zinc-100 flex flex-col"
      style={{ fontFamily: "'Plus Jakarta Sans', 'DM Sans', system-ui, sans-serif" }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&display=swap');
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: #18181b; }
        ::-webkit-scrollbar-thumb { background: #3f3f46; border-radius: 3px; }
      `}</style>

      <SiteHeader />

      <div className="border-b border-zinc-800/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link
            href="/error-explainer-tool"
            className="flex items-center gap-2 text-zinc-400 hover:text-zinc-100 transition-colors text-sm"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            Back to Explainer
          </Link>
          <div className="flex items-center gap-3">
            <Link
              href="/search"
              className="flex items-center gap-1.5 text-zinc-500 hover:text-zinc-300 transition-colors text-xs"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              Search
            </Link>
            {popularity ? (
              <span className="inline-flex items-center gap-1.5 text-cyan-300 bg-cyan-500/10 border border-cyan-500/20 rounded-full px-2 py-0.5 text-xs" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                <span className="w-1.5 h-1.5 rounded-full bg-cyan-300" />
                {popularity}
              </span>
            ) : (
              <span className="text-zinc-500 text-xs" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>Developer guide</span>
            )}
          </div>
        </div>
      </div>

      <main className="flex-1 w-full max-w-4xl mx-auto px-4 sm:px-6 py-10">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs text-zinc-500 mb-6" aria-label="Breadcrumb">
          <Link href="/error-explainer-tool" className="hover:text-zinc-300 transition-colors">Home</Link>
          <span>/</span>
          <Link href={`/languages/${language}`} className="hover:text-zinc-300 transition-colors capitalize">{language}</Link>
          <span>/</span>
          <span className="text-zinc-400 truncate max-w-xs">{errorMessage.slice(0, 50)}</span>
        </nav>

        {/* Page Header */}
        <div className="mb-8">
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${langColor}`}>
              {language.toUpperCase()}
            </span>
            {explanation && (
              <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${severity.badge}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${severity.dot}`} />
                {severity.label}
              </span>
            )}
            {errorTypeInfo && (
              <Link
                href={`/error-types/${errorTypeInfo.slug}`}
                className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border border-cyan-500/30 bg-cyan-500/10 text-cyan-400 hover:bg-cyan-500/20 transition-colors"
              >
                {errorTypeInfo.name}
              </Link>
            )}
            <span className="text-xs text-zinc-600">{formattedDate}</span>
          </div>

          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-3 leading-tight">
            {explanation?.errorType || errorMessage.slice(0, 80)}
          </h1>

          <p
            className="text-zinc-400 text-sm font-mono bg-zinc-900 border border-zinc-800 rounded-lg px-4 py-2.5 break-all"
            style={{ fontFamily: "'IBM Plex Mono', monospace" }}
          >
            {errorMessage}
          </p>
        </div>

        {explanation ? (
          <div className="space-y-6">
            {/* Meaning */}
            <section className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
              <h2 className="flex items-center gap-2 text-base font-semibold text-zinc-100 mb-3">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-cyan-400">
                  <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                What This Error Means
              </h2>
              <p className="text-zinc-300 text-sm leading-7">{explanation.meaning}</p>
            </section>

            {/* Why it happens */}
            <section className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
              <h2 className="flex items-center gap-2 text-base font-semibold text-zinc-100 mb-3">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-amber-400">
                  <circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
                Why It Happens
              </h2>
              <p className="text-zinc-300 text-sm leading-7">{explanation.whyItHappens}</p>
            </section>

            {/* How to fix */}
            <section className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
              <h2 className="flex items-center gap-2 text-base font-semibold text-zinc-100 mb-4">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-emerald-400">
                  <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
                </svg>
                How to Fix It
              </h2>
              <ol className="space-y-3">
                {explanation.howToFix.map((step, i) => (
                  <li key={i} className="flex gap-3">
                    <span
                      className="flex-shrink-0 w-5 h-5 rounded-full bg-emerald-500/15 text-emerald-400 text-xs font-bold flex items-center justify-center mt-0.5"
                      style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                    >
                      {i + 1}
                    </span>
                    <span className="text-zinc-300 text-sm leading-6">{step}</span>
                  </li>
                ))}
              </ol>
            </section>

            {/* Example Code */}
            <section className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
              <h2 className="flex items-center gap-2 text-base font-semibold text-zinc-100 mb-4">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-violet-400">
                  <polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" />
                </svg>
                Example Code Solution
              </h2>
              <div className="space-y-4">
                <div>
                  <span className="text-xs font-medium text-red-400 uppercase tracking-wide mb-2 block" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                    ❌ Before (problematic code)
                  </span>
                  <CodeBlock code={explanation.exampleCode.before} language={language as any} variant="error" />
                </div>
                <div>
                  <span className="text-xs font-medium text-emerald-400 uppercase tracking-wide mb-2 block" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                    ✅ After (fixed code)
                  </span>
                  <CodeBlock code={explanation.exampleCode.after} language={language as any} variant="success" />
                </div>
                <div className="flex gap-2 bg-zinc-800/50 rounded-xl p-3 border border-zinc-700/50">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-zinc-500 flex-shrink-0 mt-0.5">
                    <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" />
                  </svg>
                  <p className="text-zinc-400 text-xs leading-5">{explanation.exampleCode.explanation}</p>
                </div>
              </div>
            </section>

            {/* Related Errors from explanation */}
            {explanation.relatedErrors && explanation.relatedErrors.length > 0 && (
              <div className="px-1">
                <p className="text-xs font-medium text-zinc-500 uppercase tracking-wide mb-2.5">Also Related</p>
                <div className="flex flex-wrap gap-2">
                  {explanation.relatedErrors.map((err) => (
                    <span
                      key={err}
                      className="text-xs px-2.5 py-1 rounded-full bg-zinc-800 text-zinc-400 border border-zinc-700"
                      style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                    >
                      {err}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Internal Linking: Language + Error Type cluster links */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-3">
              <p className="text-xs font-medium text-zinc-500 uppercase tracking-wide">Browse Related Clusters</p>
              <div className="flex flex-wrap gap-2">
                <Link
                  href={`/languages/${language}`}
                  className={`inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border transition-all hover:opacity-80 ${langColor}`}
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" />
                  </svg>
                  All {languageInfo?.name || language.toUpperCase()} Errors
                </Link>
                {errorTypeInfo && (
                  <Link
                    href={`/error-types/${errorTypeInfo.slug}`}
                    className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border border-cyan-500/30 bg-cyan-500/10 text-cyan-400 hover:bg-cyan-500/20 transition-colors"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                    </svg>
                    All {errorTypeInfo.name}s
                  </Link>
                )}
                <Link
                  href="/error-types"
                  className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full border border-zinc-700 bg-zinc-800 text-zinc-400 hover:text-zinc-200 hover:border-zinc-600 transition-colors"
                >
                  Browse All Error Types
                </Link>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8 text-center">
            <p className="text-zinc-400 text-sm">Explanation data unavailable for this error.</p>
          </div>
        )}

        {/* Related Errors from DB */}
        {related.length > 0 && (
          <section className="mt-10">
            <h2 className="text-base font-semibold text-zinc-100 mb-4 flex items-center gap-2">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-cyan-400">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
              </svg>
              Related {language.toUpperCase()} Errors
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {related.map((rel) => (
                <Link
                  key={rel.id}
                  href={`/errors/${rel.slug}`}
                  className="group bg-zinc-900 border border-zinc-800 rounded-xl p-4 hover:border-zinc-600 hover:bg-zinc-800/50 transition-all"
                >
                  <p
                    className="text-zinc-300 text-xs font-mono truncate group-hover:text-white transition-colors mb-2"
                    style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                  >
                    {rel.error_message.slice(0, 70)}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${LANGUAGE_COLORS[rel.language] || LANGUAGE_COLORS.other}`}>
                      {rel.language.toUpperCase()}
                    </span>
                    {getPopularityLabel(rel.views) ? (
                      <span className="text-[10px] uppercase tracking-wide text-cyan-300 bg-cyan-500/10 border border-cyan-500/20 rounded-full px-2 py-0.5">
                        {getPopularityLabel(rel.views)}
                      </span>
                    ) : (
                      <span className="text-xs text-zinc-600">Guide</span>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Related Blog Articles */}
        {relatedBlogs.length > 0 && (
          <section className="mt-8">
            <h2 className="text-base font-semibold text-zinc-100 mb-4 flex items-center gap-2">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-violet-400">
                <path d="M12 20h9M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
              </svg>
              Related {language.toUpperCase()} Blog Articles
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {relatedBlogs.map((blog) => (
                <Link
                  key={blog.id}
                  href={`/blog/${blog.slug}`}
                  className="group bg-zinc-900 border border-zinc-800 rounded-xl p-4 hover:border-zinc-600 hover:bg-zinc-800/50 transition-all"
                >
                  <p className="text-zinc-300 text-xs font-medium group-hover:text-white transition-colors mb-2 line-clamp-2 leading-5">
                    {blog.title}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className={`text-xs px-2 py-0.5 rounded-full border ${LANGUAGE_COLORS[blog.language] || LANGUAGE_COLORS.other}`}>
                      {blog.language.toUpperCase()}
                    </span>
                    <span className="text-xs text-cyan-400 group-hover:text-cyan-300 transition-colors flex items-center gap-1">
                      Read article
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M5 12h14M12 5l7 7-7 7" />
                      </svg>
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* CTA */}
        <div className="mt-10 bg-zinc-900 border border-zinc-800 rounded-2xl p-6 text-center">
          <p className="text-zinc-400 text-sm mb-3">Have a different error? Get an instant explanation.</p>
          <Link
            href="/error-explainer-tool"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-cyan-500 text-zinc-950 font-semibold text-sm hover:bg-cyan-400 transition-colors"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            Explain Another Error
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-zinc-800 py-6 mt-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center text-zinc-600 text-xs">
          <p>DevError Explainer — A self-growing developer knowledge base</p>
        </div>
      </footer>
    </div>
  );
}
